import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, animate, motion, useReducedMotion } from 'framer-motion';
import { Facebook, Globe, Instagram, Linkedin, Music2, Pin, Store } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ArdiOrb } from '../../../components/design-system/common/ArdiOrb';
import { formatCompact, formatNumber } from '../../../components/design-system/admin/format';
import { LogLine, Notebook, SocialPlatform, learningProgress } from '../../../modules/vendor/onboarding/journey';
import { BrowserFrame } from './BrowserFrame';

export const SOCIAL_ICON: Record<SocialPlatform, React.ComponentType<{ className?: string }>> = {
    instagram: Instagram,
    facebook: Facebook,
    pinterest: Pin,
    tiktok: Music2,
    linkedin: Linkedin,
};

const PLATFORM_LABEL: Record<string, string> = {
    shopify: 'Shopify',
    woocommerce: 'WooCommerce',
    prestashop: 'PrestaShop',
    magento: 'Magento',
    bigcommerce: 'BigCommerce',
    wix: 'Wix',
    squarespace: 'Squarespace',
};
export const platformLabel = (p?: string | null) => (p ? PLATFORM_LABEL[p] ?? p : '');

/** A section arriving: rises a little and fades in, once. */
const arrive = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0 },
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
};

/**
 * What Ardi has learned so far, drawn as it lands.
 *
 * It sits beside every question after the website, so the vendor answers
 * while watching their business being read: the homepage appears in the
 * frame, the palette drops in swatch by swatch, the search numbers count up.
 * Nothing here is decoration — every item is something a later page uses.
 */
export const ArdiNotebook: React.FC<{ notebook: Notebook; compact?: boolean; className?: string }> = ({ notebook, compact, className }) => {
    const { t } = useTranslation();
    const nb = notebook;
    const busy = !(nb.brandDone && nb.marketDone);
    const current = [...nb.log].reverse().find((l) => l.status === 'running');
    const socials = Object.entries(nb.socials) as [SocialPlatform, NonNullable<Notebook['socials'][SocialPlatform]>][];
    const hasMarket = nb.market.keywords != null || nb.market.competitors.length > 0;
    const hasBrand = nb.brand.colors.length > 0 || nb.brand.font || nb.brand.tone;
    const progress = Math.round(learningProgress(nb) * 100);

    return (
        <section className={cn('flex flex-col overflow-hidden rounded-2xl border bg-card', className)} aria-label={t('ONBOARDING_V2.NOTEBOOK.TITLE')} data-testid="ardi-notebook">
            <header className="flex items-center gap-3 border-b px-5 py-3.5">
                <ArdiOrb size={28} state={busy ? 'thinking' : 'done'} />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{t('ONBOARDING_V2.NOTEBOOK.TITLE')}</p>
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.p key={current?.id ?? (busy ? 'busy' : 'done')} className="truncate text-xs text-muted-foreground" {...arrive}>
                            {busy ? (current ? <LogText line={current} /> : t('ONBOARDING_V2.NOTEBOOK.STARTING')) : t('ONBOARDING_V2.NOTEBOOK.DONE')}
                        </motion.p>
                    </AnimatePresence>
                </div>
                <span className="text-xs tabular-nums text-muted-foreground">{progress}%</span>
            </header>

            <div className="flex flex-col gap-5 overflow-y-auto p-5">
                {/* The site itself, filling in once the capture lands. */}
                <div className="flex flex-col gap-3">
                    <BrowserFrame url={nb.website} screenshot={nb.screenshot} className={compact ? 'max-h-40' : 'max-h-56'} />
                    <AnimatePresence>
                        {(nb.brand.name || nb.brand.promise) && (
                            <motion.div {...arrive} className="flex items-start gap-3">
                                {nb.brand.logoUrl ? (
                                    <img src={nb.brand.logoUrl} alt="" className="size-9 shrink-0 rounded-lg border bg-background object-contain p-1" />
                                ) : (
                                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background font-heading text-sm font-medium">
                                        {(nb.brand.name || '?').slice(0, 1).toUpperCase()}
                                    </span>
                                )}
                                <div className="min-w-0">
                                    {nb.brand.name && <p className="truncate text-sm font-medium">{nb.brand.name}</p>}
                                    {nb.brand.promise && <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{nb.brand.promise}</p>}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <AnimatePresence>
                    {hasBrand && (
                        <motion.div key="brand" {...arrive}>
                            <Label>{t('ONBOARDING_V2.NOTEBOOK.BRAND')}</Label>
                            {nb.brand.colors.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {nb.brand.colors.map((c, i) => (
                                        <motion.span
                                            key={c}
                                            title={c}
                                            className="size-7 rounded-lg border shadow-[inset_0_0_0_1px_rgb(0_0_0/0.04)]"
                                            style={{ background: c }}
                                            initial={{ scale: 0.3, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ delay: i * 0.08, type: 'spring', stiffness: 420, damping: 24 }}
                                        />
                                    ))}
                                </div>
                            )}
                            <div className="mt-3 flex flex-col gap-1.5 text-sm">
                                {nb.brand.font && (
                                    <p className="flex items-baseline gap-2">
                                        <span className="font-heading text-base" style={{ fontFamily: `"${nb.brand.font}", var(--font-heading, inherit)` }}>Aa</span>
                                        <span className="truncate text-muted-foreground">{nb.brand.font}</span>
                                    </p>
                                )}
                                {nb.brand.tone && (
                                    <p className="text-muted-foreground">
                                        {t('ONBOARDING_V2.NOTEBOOK.TONE')} <span className="text-foreground">{nb.brand.tone}</span>
                                    </p>
                                )}
                            </div>
                            {nb.brand.keywords.length > 0 && (
                                <div className="mt-2.5 flex flex-wrap gap-1.5">
                                    {nb.brand.keywords.map((k, i) => (
                                        <motion.span
                                            key={k}
                                            className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground"
                                            initial={{ opacity: 0, y: 4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 + i * 0.06 }}
                                        >
                                            {k}
                                        </motion.span>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {socials.length > 0 && (
                        <motion.div key="socials" {...arrive}>
                            <Label>{t('ONBOARDING_V2.NOTEBOOK.SOCIALS')}</Label>
                            <ul className="mt-2 flex flex-col gap-1.5">
                                {socials.map(([platform, s]) => {
                                    const Icon = SOCIAL_ICON[platform];
                                    return (
                                        <motion.li key={platform} layout className="flex items-center gap-2 text-sm" initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}>
                                            <Icon className="size-4 text-muted-foreground" />
                                            <span className="truncate">@{s.handle}</span>
                                            {s.followers != null && (
                                                <span className="ms-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                                                    {t('ONBOARDING_V2.NOTEBOOK.FOLLOWERS', { count: s.followers, formatted: formatCompact(s.followers) })}
                                                </span>
                                            )}
                                        </motion.li>
                                    );
                                })}
                            </ul>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {hasMarket && (
                        <motion.div key="market" {...arrive}>
                            <Label>{t('ONBOARDING_V2.NOTEBOOK.MARKET')}</Label>
                            <dl className="mt-2 grid grid-cols-3 gap-3">
                                <Figure label={t('ONBOARDING_V2.NOTEBOOK.KEYWORDS')} value={nb.market.keywords} />
                                <Figure label={t('ONBOARDING_V2.NOTEBOOK.VISITS')} value={nb.market.visits} />
                                <Figure label={t('ONBOARDING_V2.NOTEBOOK.TOP10')} value={nb.market.top10} />
                            </dl>
                            {nb.market.topKeyword && (
                                <p className="mt-3 truncate text-xs text-muted-foreground">
                                    {t('ONBOARDING_V2.NOTEBOOK.TOP_KEYWORD')} <span className="text-foreground">“{nb.market.topKeyword}”</span>
                                </p>
                            )}
                            {nb.market.competitors.length > 0 && (
                                <div className="mt-3">
                                    <p className="text-xs text-muted-foreground">{t('ONBOARDING_V2.NOTEBOOK.RIVALS')}</p>
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                        {nb.market.competitors.map((d, i) => (
                                            <motion.span
                                                key={d}
                                                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: i * 0.07 }}
                                            >
                                                <Globe className="size-3 text-muted-foreground" />
                                                {d}
                                            </motion.span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {nb.shop?.platform && (
                        <motion.div key="shop" {...arrive} className="flex items-center gap-2 text-sm">
                            <Store className="size-4 text-muted-foreground" />
                            <span>{t('ONBOARDING_V2.NOTEBOOK.SHOP', { platform: platformLabel(nb.shop.platform) })}</span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => <p className="text-xs font-medium text-muted-foreground">{children}</p>;

/** A number that counts up to where it landed — the reading arriving, not appearing. */
const Figure: React.FC<{ label: string; value?: number }> = ({ label, value }) => (
    <div className="min-w-0">
        <dd className="font-heading text-lg leading-none font-medium tabular-nums">{value == null ? '—' : <CountUp to={value} />}</dd>
        <dt className="mt-1 truncate text-[11px] text-muted-foreground">{label}</dt>
    </div>
);

export const CountUp: React.FC<{ to: number; format?: (n: number) => string }> = ({ to, format = (n) => formatNumber(n) }) => {
    const reduce = useReducedMotion();
    const [shown, setShown] = useState(reduce ? to : 0);
    const from = useRef(0);
    useEffect(() => {
        if (reduce) {
            setShown(to);
            return undefined;
        }
        const controls = animate(from.current, to, { duration: 1.1, ease: 'easeOut', onUpdate: (v) => setShown(v) });
        from.current = to;
        return () => controls.stop();
    }, [to, reduce]);
    return <>{format(Math.round(shown))}</>;
};

/** One line of the reading, in the vendor's language. */
export const LogText: React.FC<{ line: LogLine }> = ({ line }) => {
    const { t } = useTranslation();
    const p = line.params || {};
    const state = line.status === 'running' ? 'RUNNING' : line.status === 'failed' ? 'FAILED' : 'DONE';
    const key = `ONBOARDING_V2.LOG.${line.kind.toUpperCase()}.${state}`;
    const params: Record<string, string | number> = { ...p };
    for (const k of ['pages', 'colours', 'fonts', 'images', 'keywords', 'domains', 'count']) {
        if (typeof p[k] === 'number') params[`${k}Formatted`] = formatNumber(p[k] as number);
    }
    if (p.platform && line.kind === 'shop') params.platform = platformLabel(String(p.platform));
    if (p.platform && line.kind === 'social') params.network = t(`ONBOARDING_V2.SOCIALS.${String(p.platform).toUpperCase()}`);
    // The number the sentence turns on, for its plural form.
    const count = [p.count, p.pages, p.domains, p.keywords].find((v) => typeof v === 'number') as number | undefined;
    return <>{t(key, { ...params, count })}</>;
};
